import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { skillService } from '../../../../services/skillService';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const AdminSkills = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const data = await skillService?.getAllSkills();
      setSkills(data);
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;

    try {
      await skillService?.deleteSkill(id);
      setSkills(skills?.filter(skill => skill?.id !== id));
    } catch (err) {
      console.error('Error deleting skill:', err);
      alert('Failed to delete skill');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Manage Skills - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">Manage Skills</h1>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="outline" size="sm" iconName="Home" />
                </Link>
                <Button variant="ghost" size="sm" iconName="LogOut" onClick={handleSignOut} />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Skills</h2>
              <p className="text-muted-foreground">Manage all your skills</p>
            </div>
            <Link to="/admin/dashboard/skills/new">
              <Button variant="default" iconName="Plus" iconPosition="left">
                New Skill
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading skills...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">{error}</p>
            </div>
          ) : skills?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Icon name="Code2" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No skills yet</p>
              <Link to="/admin/dashboard/skills/new">
                <Button variant="default" iconName="Plus" iconPosition="left">
                  Create Your First Skill
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {skills?.map((skill) => (
                <div
                  key={skill?.id}
                  className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={skill?.icon} size={24} color="var(--color-accent)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {skill?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">Order: {skill?.displayOrder}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {skill?.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {skill?.technologies?.slice(0, 3)?.map((tech, idx) => (
                      <span key={idx} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/admin/dashboard/skills/edit/${skill?.id}`}>
                      <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => handleDelete(skill?.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminSkills;